import { useQuery } from '@tanstack/react-query';
import { ADMIN_PUBKEY, VERIFIED_LIST_D_TAG } from '@/lib/admin';
import { discoveryPool, discoverySignal } from '@/lib/appRelays';

/**
 * Fetch the list of verified merchant pubkeys.
 *
 * The admin maintains a kind 30078 addressable event with:
 * - d = "bch-verified-merchants"
 * - p tags listing each verified merchant's pubkey
 *
 * Only the admin's pubkey is trusted — this is filtered at the relay level.
 *
 * Returns a plain array (not a Set) so the result survives being serialized to
 * / restored from the persisted localStorage query cache — a Set would
 * deserialize to `{}` and lose its `.has()` method.
 */
export function useVerifiedMerchants() {
  return useQuery({
    queryKey: ['nostr', 'verified-merchants'],
    queryFn: async (c) => {
      const events = await discoveryPool.query(
        [{
          kinds: [30078],
          authors: [ADMIN_PUBKEY],
          '#d': [VERIFIED_LIST_D_TAG],
          limit: 1,
        }],
        { signal: discoverySignal(c.signal) },
      );

      if (events.length === 0) return [] as string[];

      const event = events[0];
      const verifiedPubkeys: string[] = [];

      for (const tag of event.tags) {
        if (tag[0] === 'p' && tag[1]) {
          verifiedPubkeys.push(tag[1]);
        }
      }

      return verifiedPubkeys;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes — changes rarely
  });
}
